import { createFileRoute, Link } from '@tanstack/react-router';
import { Stack, Text } from '@mantine/core';
import { motion } from 'framer-motion';
import { ScrollText, ShieldCheck, ShieldHalf, Users, UsersRound } from 'lucide-react';
import { PageContainer, PageHeader, Section, Card } from '../components/ui';
import { staggerContainer, cardItem } from '../lib/motion';
import { colors } from '../theme/tokens';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const Route = (createFileRoute as any)('/admin/')({
  component: AdminIndex,
});

const TILES = [
  {
    to: '/admin/tenants',
    icon: <ShieldCheck size={18} color={colors.hudCyan} />,
    title: 'Organizations',
    description: 'Create and manage tenants (superadmin only).',
  },
  {
    to: '/admin/users',
    icon: <Users size={18} color={colors.neon} />,
    title: 'Users',
    description: 'Invite users + assign roles inside your tenant.',
  },
  {
    to: '/admin/cohorts',
    icon: <UsersRound size={18} color={colors.hudCyan} />,
    title: 'Cohorts',
    description: 'Group trainees into cohorts and enroll them in scenarios.',
  },
  {
    to: '/admin/audit',
    icon: <ScrollText size={18} color={colors.hudAmber} />,
    title: 'Audit Log',
    description: 'Every action recorded to S3 Object-Lock (7-yr retention).',
  },
];

function AdminIndex() {
  return (
    <PageContainer size="wide">
      <PageHeader
        icon={ShieldHalf}
        title="Administration"
        description="Manage tenants, users, cohorts and audit activity across the platform."
        breadcrumbs={[{ label: 'Forge', to: '/' }, { label: 'Admin' }]}
      />
      <Section
        title="Management"
        description="Tenant + user management and the audit trail. Visible only to org admins and superadmins."
      >
        <motion.div
          variants={staggerContainer}
          initial="hidden"
          animate="show"
          className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3"
        >
          {TILES.map((tile) => (
            <motion.div key={tile.to} variants={cardItem}>
              <Tile to={tile.to} icon={tile.icon} title={tile.title} description={tile.description} />
            </motion.div>
          ))}
        </motion.div>
      </Section>
    </PageContainer>
  );
}

function Tile({ to, icon, title, description }: { to: string; icon: React.ReactNode; title: string; description: string }) {
  return (
    <Link to={to} style={{ textDecoration: 'none' }} className="block h-full">
      <Card style={{ cursor: 'pointer', height: '100%' }}>
        <Stack gap="xs">
          {icon}
          <Text fw={600}>{title}</Text>
          <Text size="sm" c="dimmed">{description}</Text>
        </Stack>
      </Card>
    </Link>
  );
}
